import type {
  AggregatedColumnJSON,
  AggregatedTableJSON,
  ApiTransformationParams,
} from '@highimpact-frontend/api-types';
import {
  tr_aggregate,
  tr_aggregation_function,
  tr_function,
} from '@highimpact-frontend/api-types';

type MeasurePipelineFunctions =
  ApiTransformationParams['pipeline']['functions'];

type AggregateFunctionName = Parameters<typeof tr_aggregation_function>[0];

type AggregationTableColumn =
  AggregatedTableJSON['rows'][number]['columns'][number];

function isAggregatedColumn(
  column: AggregationTableColumn
): column is AggregatedColumnJSON {
  return column.type === 'AggregatedColumn';
}

function transformationTypeToFunctionName(
  transformationType: AggregatedColumnJSON['transformation_type']
): AggregateFunctionName {
  switch (transformationType) {
    case 'sum':
      return 'sum';
    case 'count':
      return 'count';
    case 'mean':
      return 'mean';
    case 'min':
      return 'min';
    case 'max':
      return 'max';
    case 'median':
      return 'median';
    case 'first_quartile':
      return 'first_quartile';
    case 'third_quartile':
      return 'third_quartile';
    default:
      throw new Error(`Unsupported transformation type: ${transformationType}`);
  }
}

/** Maps each datasource index to its measure column label (one entry per source). */
export function buildAggregationTableContextIndexToName(
  config: AggregatedTableJSON
): Record<string, string> {
  const mapping: Record<string, string> = {};

  config.rows.forEach((row) => {
    const col = row.columns.find(isAggregatedColumn);
    if (!col) {
      // Rows may temporarily lack AggregatedColumns while the form is edited
      // (sources selected before aggregation types). Skip until complete.
      return;
    }

    mapping[String(col.data_source_index)] = col.column_name;
  });

  return mapping;
}

/** True when the aggregated table config defines at least one statistic column. */
export function aggregatedTableHasAggregatedColumns(
  config: AggregatedTableJSON
): boolean {
  return config.rows.some((row) => row.columns.some(isAggregatedColumn));
}

/** Aggregation-table pipeline: sort then aggregate with prefixed stat columns. */
export function buildAggregationTableMeasurePipeline(
  config: AggregatedTableJSON
): { readonly functions: MeasurePipelineFunctions } {
  const seen = new Set<string>();
  const aggregations: ReturnType<typeof tr_aggregation_function>[] = [];

  config.rows.forEach((row) => {
    row.columns.filter(isAggregatedColumn).forEach((col) => {
      const dedupeKey = `${col.transformation_type}:${col.column_name}`;
      if (seen.has(dedupeKey)) {
        return;
      }
      seen.add(dedupeKey);

      const functionName = transformationTypeToFunctionName(
        col.transformation_type
      );

      aggregations.push(
        functionName === 'quartiles'
          ? tr_aggregation_function(functionName, {
              column: col.column_name,
            })
          : tr_aggregation_function(functionName, {
              column: col.column_name,
              prefix: true,
            })
      );
    });
  });

  return {
    functions: [
      tr_function('sort', {
        criteria: [{ column: 'horodate', order: 'ascending' }],
      }),
      tr_aggregate(null, aggregations),
    ],
  };
}

/** Table-by-campaign pipeline matches bar/histogram (sort + project). */
export function buildTableByCampaignMeasurePipeline(
  contextIndexToName: Record<string, string>
): { readonly functions: MeasurePipelineFunctions } {
  const projectParams: Record<string, string> = { horodate: 'horodate' };
  for (const [index, name] of Object.entries(contextIndexToName)) {
    projectParams[name] = index;
  }

  return {
    functions: [
      tr_function('sort', {
        criteria: [{ column: 'horodate', order: 'ascending' }],
      }),
      tr_function('project', projectParams),
    ],
  };
}
